<script lang="ts">
	import { partytownSnippet } from '@builder.io/partytown/integration';
	import { onNavigate } from '$app/navigation';

	import '../app.css';
	import { page } from '$app/stores';
	import Header from '$lib/header/Header.svelte';
	import NotificationManager from '$lib/notifications/NotificationManager.svelte';
	import PopupManager from '$lib/popups/PopupManager.svelte';
	import Footer from '$lib/footer/Footer.svelte';
	import {
		ordersObject,
		ordersBeingTracked,
		pythPrices,
		markets,
		unsubmittedOrders,
		stablesPrices,
		recentFewOrderHistory
	} from '$lib/home/store';
	import { ethers } from 'ethers';
	import {
		overrideWalletAddress,
		scWalletAccountResolver,
		smartWalletCreationPending,
		walletAddress,
		walletConnected,
		walletLoaded
	} from '$lib/wallet/store';
	import { addTransactionSuccessfulNotification } from '$lib/notifications/notificationManager';
	import { getNumBackInUnits } from '$lib/util/util';
	import { checkSmartContractWalletOwners } from '$lib/home/scwallet/scWallet';
	import {
		smartContractWalletAddress,
		overrideSmartContractWalletAddress,
		checkingSmartContractWallet
	} from '$lib/home/scwallet/store';
	import MobileFooter from '$lib/footer/MobileFooter.svelte';
	import NinjaKeys from '$lib/ninja_keys/NinjaKeys.svelte';
	import type { LayoutData } from './$types';
	import { setPythData, setupPythData } from '$lib/data/pyth';
	import { setMarketWithData } from '$lib/data/markets';
	import { beforeNavigate } from '$app/navigation';
	import { updated } from '$app/stores';
	import type { PythPriceResponse } from '$lib/types/pyth';
	import type { Market } from '$lib/types/market';
	import type { Order } from '$lib/types/order';
	import { popupModalOpen, popupModalType } from '$lib/popups/popupsStore';
	import { strategyDetails } from '$lib/strategies/store';
	import { setStratergyData } from '$lib/data/strategies';
	import type { StrategyType } from '$lib/types/strategy';
	import { onMount } from 'svelte';
	import { browser } from '$app/environment';
	import { fade } from 'svelte/transition';
	import { cubicIn, cubicOut } from 'svelte/easing';
	import { initWallet, trackCurrentAccount } from '$lib/wallet/wallet';
	import { setCompetitionConfig } from '$lib/data/leaderboard';
	import {
		currentSeasonObject,
		currentUserLevel,
		isUserEligibleForNewYearOrb,
		selectedSeasonIdentifier,
		userSeasonStats
	} from '$lib/tradingSeason/store';
	import {
		getTradingSeasonConfig,
		getUserPolyPoints,
		getUserTradingSeasonStats
	} from '$lib/data/rewards';
	import { apiURL, tradeStatsApiURL } from '$lib/apis/store';
	import { positionsRaw } from '$store/positions';
	import { checkTosAndSCWallet } from '$lib/util/common';
	import { destroUserDatarefresh } from '$lib/data/refreshData';
	import { validateAddressForNewYearOrb } from '$lib/apis/rewards';

	beforeNavigate(({ willUnload, to }) => {
		if ($updated && !willUnload && to?.url) {
			location.href = to.url.href;
		}
	});

	export let data: LayoutData;

	onMount(async () => {
		if (localStorage.getItem('wc@2:core:0.3//history')) {
			localStorage.removeItem('wc@2:core:0.3//history');
		}
	});

	const { marketsData, pyth, strategiesData } = data;

	if (browser) {
		(async () => {
			await initWallet();
			trackCurrentAccount();
			await setupPythData();
		})();
	}

	if (pyth && !$pythPrices) {
		(async () => {
			setPythData(pyth as PythPriceResponse[], $stablesPrices);
		})();
	}
	if (marketsData && !$markets) {
		setMarketWithData(marketsData as Market[]);
	}

	if (strategiesData && !$strategyDetails) {
		setStratergyData(strategiesData as Array<StrategyType>);
	}

	$: if ($page.url.searchParams.get('address')) {
		let add: string = ethers.utils.getAddress($page.url.searchParams.get('address') as string);
		overrideWalletAddress.set(add);
		walletAddress.set(add);
	}

	$: if ($page.url.searchParams.get('wallet')) {
		let scWallet: string = ethers.utils.getAddress($page.url.searchParams.get('wallet') as string);
		overrideSmartContractWalletAddress.set(scWallet);
		checkSmartContractWalletOwners(scWallet as `0x${string}`, $scWalletAccountResolver).then(
			(txData) => {
				if (txData.length > 0) {
					overrideWalletAddress.set(txData[0]);
					walletAddress.set(txData[0]);
					return;
				}
				walletAddress.set('');
			}
		);
	}

	$: if ($page.url.searchParams.get('api')) {
		let url: string = $page.url.searchParams.get('api') as string;
		apiURL.set(url);
	}

	$: if ($page.url.searchParams.get('tradeStats')) {
		let url: string = $page.url.searchParams.get('tradeStats') as string;
		tradeStatsApiURL.set(url);
	}

	$: if ($ordersObject || $ordersBeingTracked) {
		//add orders to ordersBeingTracked if not already added
		if ($ordersObject) {
			Object.entries($ordersObject).forEach((e) => {
				//Add only pending orders for tracking and ignore Submitted (limit) orders
				if (e[1].status == 'Pending') {
					let temp: Record<string, Order> = $ordersBeingTracked;
					temp[e[0]] = e[1];
					ordersBeingTracked.set(temp);
				}
			});
		}

		//remove orders which were being tracked but are no longer being tracked
		if ($ordersBeingTracked) {
			let tempOrderBeingTracked = $ordersBeingTracked;
			Object.entries($ordersBeingTracked).forEach((e) => {
				if (Object.entries($ordersObject).length == 0 || !$ordersObject.hasOwnProperty(e[0])) {
					//order e[1] has been removed, send notification
					delete tempOrderBeingTracked[e[0]];
					addTransactionSuccessfulNotification(
						'Order Completed',
						`Your order for ${getNumBackInUnits(e[1].size, 18, 3)} ${
							e[1].asset
						} has been completed.`
					);
					positionsRaw.apiCall({});
				}
			});
			$ordersBeingTracked = tempOrderBeingTracked;
		}
	}

	// Remove orders from localStorage if already indexed

	$: if ($recentFewOrderHistory || $unsubmittedOrders) {
		let unsubmittedOrdersTemp = $unsubmittedOrders;
		if ($recentFewOrderHistory && $recentFewOrderHistory.orders) {
			$recentFewOrderHistory.orders.forEach((orderHistoryItem) => {
				if (orderHistoryItem.orderIdUi) {
					Object.entries(unsubmittedOrdersTemp).forEach((ue) => {
						if (ue[0].toLowerCase() == orderHistoryItem.orderIdUi.toLowerCase()) {
							delete unsubmittedOrdersTemp[ue[0]];
						}
					});
				}
			});
		}

		$unsubmittedOrders = unsubmittedOrdersTemp;
	}

	//Check, show and update lastLevelModalShown to this user address
	// Commenting out this code but we will need it for trading season 2
	// $: if (
	// 	$currentSeasonObject &&
	// 	$userSeasonStats &&
	// 	$smartContractWalletAddress &&
	// 	$currentUserLevel
	// ) {
	// 	if (
	// 		!$currentUserLevel.hasOwnProperty($smartContractWalletAddress) ||
	// 		$currentUserLevel[$smartContractWalletAddress] < $userSeasonStats.level
	// 	) {
	// 		$currentUserLevel[$smartContractWalletAddress] = $userSeasonStats.level;
	// 		if ($userSeasonStats.level && $userSeasonStats.level > 0) {
	// 			popupModalOpen.set(true);
	// 			popupModalType.set('LevelUp');
	// 		}
	// 	}
	// }

	// Show the modal for mystery orbs if it has not already been shown once (store flag in localStorage)
	const showMysteryOrbPopup = async () => {
		const isAddressEligible = await validateAddressForNewYearOrb($smartContractWalletAddress);
		isUserEligibleForNewYearOrb.set(isAddressEligible);
		if (isAddressEligible?.eligible) {
			if (!localStorage.getItem(`mysteryOrbModalShown-${$smartContractWalletAddress}`)) {
				popupModalOpen.set(true);
				popupModalType.set('MysteryOrb');
				localStorage.setItem(`mysteryOrbModalShown-${$smartContractWalletAddress}`, 'true');
			}
		}
	};
	$: if ($walletConnected && $smartContractWalletAddress) {
		showMysteryOrbPopup();
	}

	$: if ($walletConnected && $walletAddress) {
		checkTosAndSCWallet($scWalletAccountResolver);
	} else if ($smartContractWalletAddress) {
		destroUserDatarefresh();
	}

	$: if ($smartContractWalletAddress) {
		getTradingSeasonConfig($selectedSeasonIdentifier);
		getUserTradingSeasonStats($selectedSeasonIdentifier, $smartContractWalletAddress);
		getUserPolyPoints($smartContractWalletAddress);
	}

	//Cannot keep it as derived store variable because values come from 2 separate stores
	$: $smartWalletCreationPending =
		$walletLoaded &&
		$walletConnected &&
		$smartContractWalletAddress === '' &&
		!$checkingSmartContractWallet;

	onNavigate((navigation) => {
		// @ts-ignore
		if (!document.startViewTransition) return;

		return new Promise((resolve) => {
			// @ts-ignore
			document.startViewTransition(async () => {
				resolve();
				await navigation.complete;
			});
		});
	});
</script>

<!-- This part of code takes loading of Google tag manager off the browser main thread and reduces blocking time -->
<!-- PartyTown (https://partytown.builder.io/) is used to load the script inside service worker outside the main thread -->
<svelte:head>
	<script>
		partytown = {
			forward: ['dataLayer.push']
		};
	</script>

	{@html '<script>' + partytownSnippet() + '</script>'}

	<script type="text/partytown" src="datafeeds/udf/dist/bundle.js"></script>
	<script
		type="text/partytown"
		src="https://www.googletagmanager.com/gtag/js?id=G-5N2FJS2WL3"
	></script>

	<script type="text/partytown">
		window.dataLayer = window.dataLayer || [];
		function gtag() {
			dataLayer.push(arguments);
		}
		gtag('js', new Date());
		gtag('config', 'G-5N2FJS2WL3');
	</script>
</svelte:head>

<div class="min-h-screen flex flex-col w-full">
	<Header />
	<main
		class="mx-auto sm:px-0 px-2 pt-2 sm:pt-0 bg-fx-black flex flex-grow w-full"
		in:fade={{ duration: 100, delay: 50, easing: cubicOut }}
		out:fade={{ duration: 100, easing: cubicIn }}
	>
		<slot />
	</main>
	<div class="sm:block hidden sticky bottom-0 z-50">
		<Footer />
	</div>
	<div class="sm:hidden sticky bottom-0 z-50">
		<MobileFooter />
	</div>
</div>

<NotificationManager />
<PopupManager />
<NinjaKeys />
